// Settings card (office and admin, Session AUY): the subcontractor welcome pack's two settings.
//
// 1. THE CIS NOTICE BLOCK: the paragraph about the Construction Industry Scheme that every subcontractor FAQ and welcome
//    email carries (their Tax section). Editable here without a deploy; previewed (any house-rule refusal shown), then
//    saved through a two-step confirm; "Use the default" puts the default back. Audited server side. A pack already
//    drafted does not change; the next preparation reads the new text. The default is the Director's stated position and
//    is NOT yet confirmed by ELE's accountants.
// 2. THE AGREEMENT WORDING CONFIRMATION: until the Director confirms the solicitor review of the version 2.0 Subcontractor
//    Agreement, every generated agreement carries "DRAFT, NOT FOR SIGNATURE" and cannot be sent for signature. One
//    audited click confirms it; the same card puts the marking back (the way back).
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { SubcontractorDocumentsSettings } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { formatDateTime } from "../../lib/format";

type Pending = { change: { text?: string; useDefault?: true }; text: string; isDefault: boolean; ok: boolean; refusals: string[] };

const preCls = "whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong";
const linkBtn = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const fieldCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-body text-sm text-ele-strong outline-none focus:border-ele-green";
const confirmBtn = "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-green";

export function SubcontractorDocumentsSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [view, setView] = useState<SubcontractorDocumentsSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | "wording" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.settings.subcontractorDocuments.get())
      .then((v) => {
        if (!live) return;
        setView(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function preview(change: { text?: string; useDefault?: true }) {
    setBusy("preview");
    setError(null);
    setMessage(null);
    setPending(null);
    try {
      const res = await api.settings.subcontractorDocuments.putCisNotice({ ...change, preview: true });
      if (res.preview) setPending({ change, text: res.text, isDefault: res.isDefault, ok: res.ok, refusals: res.refusals });
    } catch (e) {
      setError(friendlyApiError(e, "Could not check the notice. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!pending || !pending.ok) return;
    setBusy("save");
    setError(null);
    try {
      const res = await api.settings.subcontractorDocuments.putCisNotice({ ...pending.change, preview: false });
      if (!res.preview) {
        setView(res);
        setMessage(pending.change.useDefault ? "The default notice is in use again." : "Saved. Every pack prepared from now on carries it.");
        setPending(null);
        setEditing(false);
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the notice. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function setWording(confirmed: boolean) {
    setBusy("wording");
    setError(null);
    setMessage(null);
    try {
      const res = await api.settings.subcontractorDocuments.putAgreementWording({ confirmed, preview: false });
      if (!res.preview) {
        setView(res);
        setMessage(confirmed ? "Recorded. Generated agreements no longer carry the draft marking and can be sent for signature." : "The draft marking is back on every generated agreement.");
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not record that. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  function startEditing() {
    if (!view) return;
    setText(view.cisNotice.text);
    setPending(null);
    setError(null);
    setMessage(null);
    setEditing(true);
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Subcontractor documents">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Subcontractor documents</h2>
      <p className="mb-3 text-sm text-ele-body">
        The welcome pack every new subcontractor receives (the Subcontractor Agreement with Schedule 1 from their rate card, Your Rates, a short FAQ and Getting Set Up) is
        prepared from their page. Two things about it are set here.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the settings.
        </p>
      ) : (
        <div className="space-y-5">
          <div className="space-y-3">
            <h3 className="font-heading text-base text-ele-strong">CIS notice</h3>
            <p className="text-xs text-ele-body">
              Printed in the FAQ&apos;s Tax section and the welcome email&apos;s Tax section. {view.cisNotice.isDefault ? "This is the default wording, the Director's stated position, not yet confirmed by ELE's accountants." : "This is the office's own wording."}
              {view.cisNotice.updatedAt ? ` Last changed ${formatDateTime(view.cisNotice.updatedAt)}${view.cisNotice.updatedByName ? ` by ${view.cisNotice.updatedByName}` : ""}.` : ""}
            </p>
            {!pending && (
              <pre className={preCls} data-testid="cis-notice-current">
                {view.cisNotice.text}
              </pre>
            )}
            {!editing ? (
              <button type="button" onClick={startEditing} className={linkBtn}>
                Change the notice
              </button>
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <span className={labelCls}>CIS notice</span>
                  <textarea aria-label="CIS notice" value={text} maxLength={view.cisNotice.maxLength} rows={4} onChange={(e) => setText(e.target.value)} className={fieldCls} />
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={() => preview({ text })} disabled={busy != null} className={confirmBtn}>
                    {busy === "preview" ? "Checking..." : "Check the wording"}
                  </button>
                  {!view.cisNotice.isDefault && (
                    <button type="button" onClick={() => preview({ useDefault: true })} disabled={busy != null} className={linkBtn}>
                      Use the default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setPending(null);
                    }}
                    disabled={busy != null}
                    className={linkBtn}
                  >
                    Cancel
                  </button>
                </div>
                {pending && (
                  <div className="space-y-2" data-testid="cis-notice-pending">
                    <pre className={preCls}>{pending.text}</pre>
                    {pending.refusals.length > 0 ? (
                      <ul role="alert" className="list-disc pl-5 text-sm text-ele-danger">
                        {pending.refusals.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                    ) : (
                      <ConfirmButton onConfirm={save} label="Save the notice" confirmLabel="Save? Press once more" busy={busy === "save"} busyLabel="Saving..." className={confirmBtn} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="font-heading text-base text-ele-strong">Agreement wording (template {view.agreementWording.templateVersion})</h3>
            {view.agreementWording.confirmed ? (
              <>
                <p className="text-xs text-ele-body">
                  Confirmed {formatDateTime(view.agreementWording.confirmedAt)}
                  {view.agreementWording.confirmedByName ? ` by ${view.agreementWording.confirmedByName}` : ""}. Generated agreements carry no draft marking and can be sent for signature.
                </p>
                <ConfirmButton onConfirm={() => setWording(false)} label="Put the draft marking back" confirmLabel="Put it back? Press once more" busy={busy === "wording"} busyLabel="Recording..." className={confirmBtn} />
              </>
            ) : (
              <>
                <p className="rounded-ele border border-ele-amber-line bg-ele-amber-wash px-3 py-2 text-sm text-ele-amber-wash-ink">
                  The wording is awaiting the Director&apos;s confirmation of its solicitor review. Every generated agreement carries &quot;{view.agreementWording.draftMarking}&quot; and cannot be sent for signature until this is confirmed.
                </p>
                <ConfirmButton onConfirm={() => setWording(true)} label="Confirm the wording (solicitor review done)" confirmLabel="Confirm? Press once more" busy={busy === "wording"} busyLabel="Recording..." className={confirmBtn} />
              </>
            )}
          </div>

          {message && <p className="text-sm text-ele-green">{message}</p>}
          {error && (
            <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
